<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <card class="card">
          <h4 slot="header" class="card-title">Found Games</h4>

          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <base-input
                v-model="search"
                placeholder="Search team name..."
                @input="onSearchInput"
              />
            </div>
            <div class="col-md-2">
              <base-input>
                <select class="form-control my-select" v-model="statusFilter" @change="onFilterChange">
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="valid">Valid</option>
                  <option value="false_positive">False Positive</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <select class="form-control my-select" v-model="typeFilter" @change="onFilterChange">
                  <option value="">All types</option>
                  <option value="bookie_vs_provider">Bookie vs Provider</option>
                  <option value="bookie_vs_bookie">Bookie vs Bookie</option>
                  <option value="kick_off_change">Kick-off Change</option>
                  <option value="early_kick_off">Early Kick-off</option>
                  <option value="to_finish">To Finish</option>
                  <option value="postponed">Postponed</option>
                  <option value="game_found">Game Found</option>
                  <option value="awarded">Awarded</option>
                  <option value="bookie_diff">Bookie Diff</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <select class="form-control my-select" v-model="channelFilter" @change="onFilterChange" :disabled="loadingChannels">
                  <option value="">All channels</option>
                  <option
                    v-for="channel in channels"
                    :key="channel.telegram_chat_id"
                    :value="channel.telegram_chat_id"
                  >{{ channel.telegram_channel_name || channel.telegram_chat_id }}</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-2 text-right">
              <button class="btn btn-primary btn-sm" @click="fetchGames" :disabled="refreshing">
                <i class="tim-icons icon-refresh-02" :class="{ 'spin': refreshing }"></i>
                Refresh
              </button>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-4">
            <span>Loading...</span>
          </div>

          <!-- Table -->
          <div v-else class="table-responsive">
            <table class="table tablesorter">
              <thead class="text-primary">
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Game 1</th>
                  <th>Game 2</th>
                  <th>Diff</th>
                  <th>Reviewed At</th>
                  <th>Found At</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="games.length === 0">
                  <td colspan="11" class="text-center">No records found.</td>
                </tr>
                <tr v-for="game in games" :key="game.id">
                  <td>{{ game.id }}</td>
                  <td>
                    <span class="badge" :class="typeBadgeClass(game.type)">
                      {{ formatType(game.type) }}
                    </span>
                  </td>
                  <td>{{ game.telegram_channel_name || '—' }}</td>
                  <td>
                    <div v-if="game.game1">
                      <a v-if="game.game1.url" :href="game.game1.url" target="_blank" rel="noopener noreferrer">
                        <strong>{{ game.game1.home_team }} vs {{ game.game1.away_team }}</strong>
                      </a>
                      <strong v-else>{{ game.game1.home_team }} vs {{ game.game1.away_team }}</strong>
                      <br>
                      <span class="game-meta">{{ game.game1.bookie }} · {{ formatKickOff(game.game1.kick_off) }}</span>
                    </div>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <div v-if="game.game2">
                      <a v-if="game.game2.url" :href="game.game2.url" target="_blank" rel="noopener noreferrer">
                        <strong>{{ game.game2.home_team }} vs {{ game.game2.away_team }}</strong>
                      </a>
                      <strong v-else>{{ game.game2.home_team }} vs {{ game.game2.away_team }}</strong>
                      <br>
                      <span class="game-meta">{{ game.game2.bookie }} · {{ formatKickOff(game.game2.kick_off) }}</span>
                    </div>
                    <span v-else>—</span>
                  </td>
                  <td>{{ game.time_difference_human !== null ? game.time_difference_human : '—' }}</td>
                  <td>{{ game.reviewed_at ? formatDateTime(game.reviewed_at) : '—' }}</td>
                  <td>{{ formatDateTime(game.created_at) }}</td>
                  <td>
                    <select
                      class="form-control my-select status-select"
                      :class="statusSelectClass(game.review_status)"
                      :value="game.review_status"
                      :disabled="updatingId === game.id"
                      @change="updateStatus(game, $event.target.value)"
                    >
                      <option value="pending">Pending</option>
                      <option value="valid">Valid</option>
                      <option value="false_positive">False Positive</option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="row mt-3 align-items-center">
            <div class="col-md-4">
              <small class="text-muted">
                Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }} records
              </small>
            </div>
            <div class="col-md-4 text-center">
              <button
                class="btn btn-sm btn-default"
                :disabled="pagination.current_page <= 1"
                @click="goToPage(pagination.current_page - 1)"
              >&laquo; Prev</button>
              <span class="mx-2" style="font-size: 13px;">
                Page {{ pagination.current_page }} / {{ pagination.last_page }}
              </span>
              <button
                class="btn btn-sm btn-default"
                :disabled="pagination.current_page >= pagination.last_page"
                @click="goToPage(pagination.current_page + 1)"
              >Next &raquo;</button>
            </div>
            <div class="col-md-4 text-right">
              <span style="font-size: 13px; margin-right: 8px;">Rows per page:</span>
              <select class="form-control my-select d-inline-block" style="width: auto;" v-model="perPage" @change="onFilterChange">
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

        </card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import formatDateTime from "../mixin/formatDateTime";

export default {
  name: "FoundGames",
  mixins: [formatDateTime],
  data() {
    return {
      games: [],
      channels: [],
      loading: false,
      refreshing: false,
      loadingChannels: false,
      updatingId: null,
      search: "",
      statusFilter: "",
      typeFilter: "",
      channelFilter: "",
      perPage: "25",
      searchTimeout: null,
      autoRefreshInterval: null,
      pagination: {
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
        total: 0,
      },
    };
  },
  mounted() {
    this.fetchChannels();
    this.fetchGames();
    this.scheduleAutoRefresh();
  },
  beforeDestroy() {
    clearTimeout(this.autoRefreshInterval);
  },
  methods: {
    fetchChannels() {
      this.loadingChannels = true;
      axios
        .get("/potential-games/channels")
        .then((response) => {
          this.channels = response.data;
        })
        .catch(() => {
          // non-critical, silently ignore
        })
        .finally(() => {
          this.loadingChannels = false;
        });
    },
    scheduleAutoRefresh() {
      this.autoRefreshInterval = setTimeout(() => {
        this.fetchGames().finally(() => this.scheduleAutoRefresh());
      }, 10 * 1000);
    },
    fetchGames() {
      this.refreshing = true;
      if (this.games.length === 0) this.loading = true;
      const params = {
        page: this.pagination.current_page,
        per_page: this.perPage,
        // sort_by: "created_at",
        // sort_direction: "desc",
      };
      if (this.search) params.search = this.search;
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.typeFilter) params.type = this.typeFilter;
      if (this.channelFilter) params.telegram_chat_id = this.channelFilter;

      return axios
        .get("/potential-games", { params })
        .then((response) => {
          const data = response.data;
          this.games = data.data;
          this.pagination = {
            current_page: data.current_page,
            last_page: data.last_page,
            from: data.from || 0,
            to: data.to || 0,
            total: data.total,
          };
        })
        .catch(() => {
          this.$toast.error("Failed to load Found Games.");
        })
        .finally(() => {
          this.loading = false;
          this.refreshing = false;
        });
    },
    updateStatus(game, newStatus) {
      if (newStatus === game.review_status) return;
      this.updatingId = game.id;
      axios
        .patch(`/potential-games/${game.id}`, { review_status: newStatus })
        .then((response) => {
          const index = this.games.findIndex((g) => g.id === game.id);
          if (index !== -1) {
            this.$set(this.games, index, response.data);
          }
          this.$toast.success(`Status updated to "${this.formatStatus(newStatus)}".`);
        })
        .catch(() => {
          this.$toast.error("Failed to update status.");
        })
        .finally(() => {
          this.updatingId = null;
        });
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchGames();
      }, 400);
    },
    onFilterChange() {
      this.pagination.current_page = 1;
      this.fetchGames();
    },
    goToPage(page) {
      this.pagination.current_page = page;
      this.fetchGames();
    },
    formatType(type) {
      const map = {
        bookie_vs_provider: "Bookie vs Provider",
        bookie_vs_bookie: "Bookie vs Bookie",
        kick_off_change: "KO Change",
        early_kick_off: "Early KO",
        to_finish: "To Finish",
        postponed: "Postponed",
        game_found: "Game Found",
        awarded: "Awarded",
        bookie_diff: "Bookie Diff",
      };
      return map[type] || type;
    },
    formatStatus(status) {
      const map = {
        pending: "Pending",
        valid: "Valid",
        false_positive: "False Positive",
      };
      return map[status] || status;
    },
    formatKickOff(kickOff) {
      if (!kickOff) return "—";
      return kickOff.substring(0, 16);
    },
    typeBadgeClass(type) {
      const map = {
        bookie_vs_provider: "badge-info",
        bookie_vs_bookie: "badge-primary",
        kick_off_change: "badge-warning",
        early_kick_off: "badge-warning",
        to_finish: "badge-secondary",
        postponed: "badge-danger",
        game_found: "badge-success",
        awarded: "badge-light",
        bookie_diff: "badge-dark",
      };
      return map[type] || "badge-secondary";
    },
    statusSelectClass(status) {
      const map = {
        pending: "status-pending",
        valid: "status-valid",
        false_positive: "status-false-positive",
      };
      return map[status] || "";
    },
  },
};
</script>

<style scoped>
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
}
.badge-info { background-color: #17a2b8; color: #fff; }
.badge-primary { background-color: #007bff; color: #fff; }
.badge-warning { background-color: #ffc107; color: #212529; }
.badge-danger { background-color: #dc3545; color: #fff; }
.badge-success { background-color: #28a745; color: #fff; }
.badge-secondary { background-color: #6c757d; color: #fff; }
.badge-light { background-color: #f8f9fa; color: #212529; }
.badge-dark { background-color: #343a40; color: #fff; }
.d-flex { display: flex; }
.status-select {
  font-size: 12px;
  padding: 3px 6px;
  height: auto;
  min-width: 120px;
  font-weight: 600;
  border-radius: 4px;
}
.status-pending  { border-color: #b8960c; color: #b8960c; }
.game-meta { font-size: 13px; color: #adb5bd; }
.status-valid    { border-color: #28a745; color: #28a745; }
.status-false-positive { border-color: #dc3545; color: #dc3545; }
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
